import {
    ActionButton,
    ButtonGroup,
    Checkbox,
    CheckboxGroup,
    Content,
    Dialog,
    DialogTrigger,
    Divider,
    Footer,
    Heading,
    Item,
    Menu,
    MenuTrigger,
    ProgressBar,
    TabList,
    TabPanels,
    Tabs,
    Text,
} from '@adobe/react-spectrum';
import React from 'react';
import { getDomainPrefix } from '../../../data/DynamoConnector';
import { getAnalytics, logEvent } from 'firebase/analytics';
import './PrintDialog.css';
import Download from '@spectrum-icons/workflow/Download';
import Merge from '@spectrum-icons/workflow/Merge';
import GraphBarVertical from '@spectrum-icons/workflow/GraphBarVertical';
import isMobile from 'ismobilejs';

export function PrintDialog(props) {
    const { deckId, authorId, pageType, name } = props;

    const [selectedOptions, setSelectedOptions] = React.useState([
        'wincons',
        'saltiness',
        'background',
        'powerlevel',
    ]);
    const [formatOptions, setFormatOptions] = React.useState(pageType === 'preview' ? 'png' : 'pdf');
    const [tab, setTab] = React.useState('bracket');
    const [isFetching, setIsFetching] = React.useState(false);

    const mobile = isMobile(window.navigator).phone;
    // On desktop the dialog sizes to its content — the preview image is
    // height-capped and the rest of the body is short, so a fixed 720px
    // left dead space below the panels.
    const dialogHeight = mobile ? 'calc(100vh - 50px)' : undefined;
    const dialogWidth = mobile ? 'calc(100vw - 20px)' : '880px';

    const triggerLabel = pageType === 'preview' ? 'Rule-zero card' : 'Rule-zero cards';
    const triggerTitle = `Download ${triggerLabel.toLowerCase()}`;

    const handleDownloadPress = async (closeMethod) => {
        setIsFetching(true);
        const options = {
            id: deckId || authorId,
            isAuthor: pageType === 'author',
            isUser: pageType === 'user',
            format: formatOptions,
            displayOptions: {},
        };

        selectedOptions.forEach((opt) => {
            options.displayOptions[opt] = true;
        });

        const url = `${getDomainPrefix()}/${tab === 'bracket' ? 'generatebracket' : 'generateadvanced'}`;
        logEvent(getAnalytics(), `${pageType}_print_${tab.toUpperCase()}_click`);

        try {
            const startTime = performance.now();
            await fetch(url, {
                method: 'POST',
                headers: { 'Content-type': 'application/json;charset=UTF-8' },
                body: JSON.stringify(options),
            })
                .then((res) => res.blob())
                .then((data) => {
                    const a = document.createElement('a');
                    a.href = window.URL.createObjectURL(data);
                    a.download = `Rule Zero - ${name}.${formatOptions}`;
                    a.click();
                });
            const endTime = performance.now();
            logEvent(getAnalytics(), 'generate_time', { timing: endTime - startTime });
            setIsFetching(false);
            closeMethod();
        } catch (error) {
            setIsFetching(false);
            logEvent(getAnalytics(), 'generate_failed');
        }
    };

    const getImagePath = () => {
        let path = `/resources/printPreviews/EXAMPLE_rule_zero_${tab}`;
        if (tab === 'powerlevel') {
            const flags = new Set(selectedOptions);
            if (flags.has('background')) path += '_bg';
            if (flags.has('powerlevel')) path += '_pl';
            if (flags.has('saltiness')) path += '_sl';
            if (flags.has('wincons')) path += '_wc';
        } else if (tab === 'bracket' && selectedOptions.includes('background')) {
            path += '_bg';
        }
        return `${path}.png`;
    };

    return (
        <DialogTrigger type={'modal'}>
            <ActionButton isDisabled={!(deckId || authorId)} aria-label={triggerTitle}>
                <span className="bcBtnLabel">{triggerLabel}</span>
                <Download />
            </ActionButton>
            {(close) => (
                <Dialog width={dialogWidth} height={dialogHeight} UNSAFE_className="printDialog">
                    <Heading>
                        <span className="printDialogEyebrow">Rule Zero Card</span>
                        <span className="printDialogTitle">
                            {pageType === 'preview' ? 'Single deck' : 'All decks'}
                            {name ? ` · ${name}` : ''}
                        </span>
                    </Heading>
                    <Divider />
                    <Content>
                        {isFetching ? (
                            <div className="printDialogLoading">
                                <ProgressBar
                                    label="GENERATING…"
                                    UNSAFE_style={{ width: '100%' }}
                                    isIndeterminate
                                />
                            </div>
                        ) : (
                            <div className="printDialogBody">
                                <section className="printDialogConfig">
                                    <Tabs
                                        defaultSelectedKey="bracket"
                                        selectedKey={tab}
                                        isEmphasized
                                        UNSAFE_className="printDialogTabs"
                                        onSelectionChange={(evn) => setTab(evn)}
                                    >
                                        <TabList marginStart="0">
                                            <Item key="bracket">
                                                <Merge size="XS" />
                                                <Text>BRACKET</Text>
                                            </Item>
                                            <Item key="powerlevel">
                                                <GraphBarVertical size="XS" />
                                                <Text>POWER LEVEL</Text>
                                            </Item>
                                        </TabList>
                                        <TabPanels marginTop="16px">
                                            <Item key="bracket">
                                                <div className="printDialogOptionsGroup">
                                                    <span className="printDialogEyebrow printDialogEyebrow--sub">
                                                        Display options
                                                    </span>
                                                    <CheckboxGroup
                                                        aria-label="Display options"
                                                        value={selectedOptions}
                                                        onChange={setSelectedOptions}
                                                    >
                                                        <Checkbox value="background">
                                                            Background image
                                                        </Checkbox>
                                                    </CheckboxGroup>
                                                </div>
                                            </Item>
                                            <Item key="powerlevel">
                                                <div className="printDialogOptionsGroup">
                                                    <span className="printDialogEyebrow printDialogEyebrow--sub">
                                                        Display options
                                                    </span>
                                                    <CheckboxGroup
                                                        aria-label="Display options"
                                                        value={selectedOptions}
                                                        onChange={setSelectedOptions}
                                                    >
                                                        <Checkbox value="background">
                                                            Background image
                                                        </Checkbox>
                                                        <Checkbox value="powerlevel">
                                                            Power level
                                                        </Checkbox>
                                                        <Checkbox value="saltiness">
                                                            Saltiness
                                                        </Checkbox>
                                                        <Checkbox value="wincons">Wincons</Checkbox>
                                                    </CheckboxGroup>
                                                </div>
                                            </Item>
                                        </TabPanels>
                                    </Tabs>
                                </section>
                                <section className="printDialogPreview">
                                    <span className="printDialogEyebrow printDialogEyebrow--sub">
                                        Preview
                                    </span>
                                    <div className="printDialogPreviewFrame">
                                        <img
                                            src={getImagePath()}
                                            alt="Rule zero card preview"
                                            className="printDialogPreviewImage"
                                        />
                                    </div>
                                </section>
                            </div>
                        )}
                    </Content>
                    {pageType === 'preview' && (
                        <Footer>
                            <div className="printDialogFormat">
                                <span className="printDialogFormatLabel">Format</span>
                                <MenuTrigger>
                                    <ActionButton isDisabled={isFetching}>
                                        {(formatOptions || '').toUpperCase()}
                                    </ActionButton>
                                    <Menu
                                        items={[
                                            { id: 'png', name: 'PNG' },
                                            { id: 'pdf', name: 'PDF' },
                                        ]}
                                        selectedKeys={formatOptions}
                                        onAction={(evn) => setFormatOptions(evn)}
                                        selectionMode="single"
                                    >
                                        {(item) => <Item key={item.id}>{item.name}</Item>}
                                    </Menu>
                                </MenuTrigger>
                            </div>
                        </Footer>
                    )}
                    <ButtonGroup>
                        <ActionButton aria-label="Cancel" isDisabled={isFetching} onPress={close}>
                            Close
                        </ActionButton>
                        <ActionButton
                            isDisabled={isFetching}
                            aria-label="Download"
                            onPress={() => handleDownloadPress(close)}
                            UNSAFE_className="printDialogDownloadBtn"
                        >
                            <Download />
                            <Text>Download</Text>
                        </ActionButton>
                    </ButtonGroup>
                </Dialog>
            )}
        </DialogTrigger>
    );
}
